/**
 * PREMIUM FOOTER PRO — assets/premium-footer.js
 *
 * Pure vanilla JS — zero Liquid tags.
 * All settings are read from data-* attributes on:
 *   <footer class="premium-footer"
 *     data-logo-gap="15"
 *     data-enable-loop="true"
 *     data-enable-autoplay="false"
 *     data-autoplay-speed="5">
 *
 * Carousel data attributes (on .pf-carousel):
 *   data-total, data-desktop, data-tablet, data-mobile
 */

(function () {
  'use strict';

  /* ─────────────────────────────────────────────
     READ FOOTER-LEVEL CONFIG
  ───────────────────────────────────────────── */
  function getFooterConfig() {
    var footer = document.querySelector('.premium-footer');
    if (!footer) return {};

    return {
      logoGap:       parseInt(footer.dataset.logoGap, 10)    || 15,
      enableLoop:    footer.dataset.enableLoop    !== 'false',
      enableAutoplay:footer.dataset.enableAutoplay === 'true',
      autoplaySpeed: parseInt(footer.dataset.autoplaySpeed, 10) || 5
    };
  }

  /* ─────────────────────────────────────────────
     CAROUSEL FACTORY
     @param {Object} opts
       carouselEl  — .pf-carousel element
       prevBtn     — previous <button> or null
       nextBtn     — next <button> or null
       dotsEl      — dots container element or null
       config      — footer-level config object
  ───────────────────────────────────────────── */
  function createCarousel(opts) {
    var carousel    = opts.carouselEl;
    var prevBtn     = opts.prevBtn;
    var nextBtn     = opts.nextBtn;
    var dotsEl      = opts.dotsEl;
    var config      = opts.config;

    var track = carousel.querySelector('.pf-track');
    if (!track) return;

    // Only real items (not skeletons)
    var allItems    = Array.from(track.querySelectorAll('.pf-logo-item'));
    var items       = allItems.filter(function (el) {
      return !el.classList.contains('pf-logo-item--skeleton');
    });

    if (items.length === 0) return;

    // Responsive breakpoints from data attributes
    var desktopPer  = parseInt(carousel.dataset.desktop, 10) || 4;
    var tabletPer   = parseInt(carousel.dataset.tablet,  10) || 3;
    var mobilePer   = parseInt(carousel.dataset.mobile,  10) || 2;

    var itemsPerPage;
    var currentPage = 0;
    var totalPages;
    var autoplayTimer = null;

    /* ── Helpers ── */

    function getItemsPerPage() {
      if (window.innerWidth <= 768)  return mobilePer;
      if (window.innerWidth <= 1024) return tabletPer;
      return desktopPer;
    }

    function getLogoGap() {
      return config.logoGap;
    }

    function calcItemWidth() {
      var carouselWidth = carousel.offsetWidth;
      var gap           = getLogoGap();
      var totalGaps     = (itemsPerPage - 1) * gap;
      return (carouselWidth - totalGaps) / itemsPerPage;
    }

    function setItemWidths() {
      var w = calcItemWidth();
      items.forEach(function (item) {
        item.style.width     = w + 'px';
        item.style.flexShrink = '0';
      });
    }

    /* ── Dots ── */

    function buildDots() {
      if (!dotsEl) return;

      if (totalPages <= 1) {
        dotsEl.style.display = 'none';
        dotsEl.innerHTML = '';
        return;
      }

      dotsEl.style.display = 'flex';
      dotsEl.innerHTML = '';

      for (var i = 0; i < totalPages; i++) {
        var dot = document.createElement('button');
        dot.className = 'pf-dot' + (i === 0 ? ' is-active' : '');
        dot.setAttribute('aria-label', 'Go to page ' + (i + 1));
        dot.setAttribute('type', 'button');
        (function (index) {
          dot.addEventListener('click', function () { goToPage(index); });
        })(i);
        dotsEl.appendChild(dot);
      }
    }

    function syncDots() {
      if (!dotsEl) return;
      var dots = dotsEl.querySelectorAll('.pf-dot');
      dots.forEach(function (dot, i) {
        dot.classList.toggle('is-active', i === currentPage);
      });
    }

    /* ── Navigation state ── */

    function syncButtons() {
      if (prevBtn) {
        prevBtn.classList.toggle('is-disabled', currentPage === 0);
      }
      if (nextBtn) {
        nextBtn.classList.toggle('is-disabled', currentPage >= totalPages - 1);
      }
    }

    /* ── Core update ── */

    function render() {
      var itemWidth  = calcItemWidth();
      var gap        = getLogoGap();
      var offset     = currentPage * itemsPerPage;
      var translateX = -(offset * (itemWidth + gap));

      track.style.transform = 'translateX(' + translateX + 'px)';
      syncButtons();
      syncDots();
    }

    function goToPage(page) {
      currentPage = Math.max(0, Math.min(page, totalPages - 1));
      render();
    }

    function nextPage() {
      if (currentPage < totalPages - 1) {
        currentPage++;
        render();
      } else if (config.enableLoop) {
        currentPage = 0;
        render();
      }
    }

    function prevPage() {
      if (currentPage > 0) {
        currentPage--;
        render();
      } else if (config.enableLoop) {
        currentPage = totalPages - 1;
        render();
      }
    }

    /* ── Autoplay ── */

    function startAutoplay() {
      if (!config.enableAutoplay) return;
      autoplayTimer = setInterval(nextPage, config.autoplaySpeed * 1000);
    }

    function stopAutoplay() {
      if (autoplayTimer) {
        clearInterval(autoplayTimer);
        autoplayTimer = null;
      }
    }

    /* ── Touch / swipe ── */

    var touchStartX = 0;

    carousel.addEventListener('touchstart', function (e) {
      touchStartX = e.changedTouches[0].screenX;
    }, { passive: true });

    carousel.addEventListener('touchend', function (e) {
      var touchEndX = e.changedTouches[0].screenX;
      var threshold = 50;
      if (touchEndX < touchStartX - threshold) nextPage();
      if (touchEndX > touchStartX + threshold) prevPage();
    }, { passive: true });

    /* ── Autoplay pause on hover ── */

    if (config.enableAutoplay) {
      carousel.addEventListener('mouseenter', stopAutoplay);
      carousel.addEventListener('mouseleave', startAutoplay);
    }

    /* ── Button listeners ── */

    if (prevBtn) prevBtn.addEventListener('click', prevPage);
    if (nextBtn) nextBtn.addEventListener('click', nextPage);

    /* ── Responsive recalculate ── */

    function recalculate() {
      var newPer = getItemsPerPage();

      if (newPer !== itemsPerPage) {
        itemsPerPage = newPer;
        totalPages   = Math.ceil(items.length / itemsPerPage);
        currentPage  = Math.min(currentPage, totalPages - 1);
        buildDots();
      }

      setItemWidths();
      render();
    }

    var resizeTimer;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(recalculate, 150);
    });

    /* ── Init ── */

    itemsPerPage = getItemsPerPage();
    totalPages   = Math.ceil(items.length / itemsPerPage);
    setItemWidths();
    buildDots();
    render();
    startAutoplay();
  }

  /* ─────────────────────────────────────────────
     BOOTSTRAP — runs after DOM is ready
  ───────────────────────────────────────────── */
  function init() {
    var config = getFooterConfig();

    /* Certifications carousel */
    var certCarousel = document.querySelector('.pf-cert-carousel');
    if (certCarousel) {
      createCarousel({
        carouselEl: certCarousel,
        prevBtn:    document.querySelector('.pf-cert-prev'),
        nextBtn:    document.querySelector('.pf-cert-next'),
        dotsEl:     document.querySelector('.pf-cert-dots'),
        config:     config
      });
    }

    /* Partners carousel */
    var partnerCarousel = document.querySelector('.pf-partner-carousel');
    if (partnerCarousel) {
      createCarousel({
        carouselEl: partnerCarousel,
        prevBtn:    document.querySelector('.pf-partner-prev'),
        nextBtn:    document.querySelector('.pf-partner-next'),
        dotsEl:     document.querySelector('.pf-partner-dots'),
        config:     config
      });
    }
  }

  /* Support both DOMContentLoaded and deferred script execution */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();